<template>
  <div class="hist-list">
    <!-- 筛选下拉 -->
    <div class="hist-filter">
      <select v-model="filterType" class="filter-sel">
        <option value="">全部</option>
        <option value="quick_video">视频</option>
        <option value="quick_image">图片</option>
      </select>
      <button type="button" class="more-btn" title="更多历史" @click="$emit('navigate', '/history')">
        更多…
      </button>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="hist-hint">加载中…</div>

    <!-- 空状态 -->
    <div v-else-if="!items.length" class="hist-hint">暂无记录</div>

    <!-- 对话列表 -->
    <div v-else class="hist-items">
      <div
        v-for="item in items"
        :key="item.id"
        class="hist-item"
        :class="{ active: activeId === item.id }"
        @click="$emit('navigate', item.type === 'quick_video' ? '/quick-video?id=' + item.id : '/quick-image?id=' + item.id)"
      >
        <!-- 类型图标 -->
        <span class="hist-icon" v-html="typeIcon(item.type)" />

        <!-- 标题（可编辑） -->
        <span
          v-if="editingId !== item.id"
          class="hist-title"
          :title="item.title"
          @dblclick.stop="startEdit(item)"
        >{{ truncateTitle(item.title) }}</span>
        <input
          v-else
          ref="editInput"
          v-model="editTitle"
          class="hist-edit-input"
          type="text"
          maxlength="60"
          @blur="commitEdit(item)"
          @keydown.enter="commitEdit(item)"
          @keydown.escape="cancelEdit"
          @click.stop
        />

        <!-- 删除按钮 -->
        <button
          type="button"
          class="hist-del"
          title="删除"
          @click.stop="confirmDelete(item)"
        >×</button>
      </div>
    </div>

    <!-- 删除确认弹窗（简化） -->
    <div v-if="deleteTarget" class="mask" @click.self="deleteTarget = null">
      <div class="confirm-dialog">
        <p>确认删除「{{ deleteTarget.title }}」？<br><small>对话内所有生成记录将一并清除。</small></p>
        <div class="confirm-ft">
          <button type="button" @click="deleteTarget = null">取消</button>
          <button type="button" class="danger" :disabled="deleting" @click="doDelete">{{ deleting ? '删除中…' : '删除' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { userApi } from "../api/user";

export default {
  name: "HistoryConversationList",
  props: {
    isLogin: { type: Boolean, default: false },
    userId: { type: [Number, String], default: "" }
  },
  data() {
    return {
      items: [],
      loading: false,
      filterType: "",
      activeId: null,
      editingId: null,
      editTitle: "",
      deleteTarget: null,
      deleting: false
    };
  },
  watch: {
    isLogin(val) {
      if (val) this.load();
      else this.items = [];
    },
    filterType() {
      this.load();
    },
    // 路由变化时刷新列表与高亮项：用户在 CreatePage 创建并跳到 /quick-image?id=X 等场景下
    // 必须实时反映新的对话，避免「新建对话未出现在左侧列表」的问题
    $route: {
      immediate: false,
      handler() {
        if (this.isLogin) this.load();
        else {
          this.items = [];
          this.activeId = null;
        }
      }
    }
  },
  created() {
    // 业务页面（CreatePage、QuickImagePage、VideoQuickCreatePage）在「首次生成创建新对话」后
    // 主动发出 conversations-changed，这里触发一次刷新以立即同步左侧历史。
    if (this.$root && this.$root.$on) {
      this.$root.$on("conversations-changed", this.load);
    }
  },
  beforeDestroy() {
    if (this.$root && this.$root.$off) {
      this.$root.$off("conversations-changed", this.load);
    }
  },
  mounted() {
    if (this.isLogin) this.load();
  },
  methods: {
    truncateTitle(title) {
      const s = String(title || "").trim();
      return s.length > 16 ? s.slice(0, 16) + "…" : s;
    },
    typeIcon(type) {
      const svg = (body) =>
        `<svg class="hist-icon-svg" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">${body}</svg>`;
      if (type === "quick_video") {
        return svg(
          '<rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" stroke-width="1.8"/><path d="M10 10.5v5l5.5-2.5L10 10.5z" fill="currentColor"/>'
        );
      }
      if (type === "quick_image") {
        return svg(
          '<rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" stroke-width="1.8"/><circle cx="8.5" cy="10" r="1.5" fill="currentColor"/><path d="M21 17l-6-6-4 4-3-3-5 5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>'
        );
      }
      return svg(
        '<circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8"/>'
      );
    },
    async load() {
      if (!this.isLogin) return;
      this.loading = true;
      try {
        const res = await userApi.recentConversations(this.filterType, 10);
        const list = (res && res.items) || (Array.isArray(res) ? res : []);
        this.items = list;
        // 同步 activeId
        const idParam = this.$route && this.$route.query && this.$route.query.id;
        this.activeId = idParam ? Number(idParam) : null;
      } catch (e) {
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    startEdit(item) {
      this.editingId = item.id;
      this.editTitle = String(item.title || "").trim();
      this.$nextTick(() => {
        const inputs = this.$refs.editInput;
        const inp = Array.isArray(inputs) ? inputs[0] : inputs;
        if (inp) inp.focus();
      });
    },
    async commitEdit(item) {
      if (this.editingId !== item.id) return;
      const title = String(this.editTitle || "").trim();
      if (title && title !== item.title) {
        try {
          await userApi.updateConversation({ id: item.id, title });
          item.title = title;
        } catch (e) {
          window.alert((e && e.message) || "更新失败");
        }
      }
      this.editingId = null;
      this.editTitle = "";
    },
    cancelEdit() {
      this.editingId = null;
      this.editTitle = "";
    },
    confirmDelete(item) {
      this.deleteTarget = item;
    },
    async doDelete() {
      if (!this.deleteTarget) return;
      this.deleting = true;
      try {
        await userApi.deleteConversation({ id: this.deleteTarget.id });
        this.items = this.items.filter(x => x.id !== this.deleteTarget.id);
        this.deleteTarget = null;
        // 如果当前页面就是被删除的对话，跳回创作页
        const curId = this.$route && this.$route.query && this.$route.query.id;
        if (curId && Number(curId) === this.deleteTarget.id) {
          this.$emit("navigate", "/create");
        }
      } catch (e) {
        window.alert((e && e.message) || "删除失败");
      } finally {
        this.deleting = false;
      }
    }
  }
};
</script>

<style scoped>
.hist-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.hist-filter {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 4px 2px;
}

.filter-sel {
  flex: 1;
  height: 24px;
  background: #2a2a2a;
  border: 1px solid #3a3a3a;
  border-radius: 6px;
  color: #e9e9e9;
  font-size: 14px;
  padding: 0 4px;
  cursor: pointer;
  appearance: none;
}

.more-btn {
  flex-shrink: 0;
  height: 24px;
  padding: 0 6px;
  background: #2a2a2a;
  border: 1px solid #3a3a3a;
  border-radius: 6px;
  color: #9ca3af;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
}

.hist-hint {
  text-align: center;
  color: #6b7280;
  font-size: 14px;
  padding: 8px 4px;
}

.hist-items {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 260px;
  overflow-y: auto;
}

.hist-items::-webkit-scrollbar {
  width: 4px;
}
.hist-items::-webkit-scrollbar-track {
  background: transparent;
}
.hist-items::-webkit-scrollbar-thumb {
  background: #444;
  border-radius: 2px;
}

.hist-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 6px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.12s;
  min-width: 0;
}

.hist-item:hover {
  background: #2a2a2a;
}

.hist-item.active {
  background: #2a3a2a;
}

.hist-icon {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hist-icon :deep(.hist-icon-svg) {
  width: 16px;
  height: 16px;
}

.hist-title {
  flex: 1;
  font-size: 14px;
  color: #d1d5db;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  user-select: none;
}

.hist-edit-input {
  flex: 1;
  height: 18px;
  background: #3a3a3a;
  border: 1px solid #19b06b;
  border-radius: 4px;
  color: #e9e9e9;
  font-size: 14px;
  padding: 0 4px;
  outline: none;
}

.hist-del {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  background: transparent;
  border: none;
  border-radius: 3px;
  color: #6b7280;
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.15s, color 0.15s;
}

.hist-item:hover .hist-del {
  opacity: 1;
}

.hist-del:hover {
  color: #e57373;
}

.mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
}

.confirm-dialog {
  background: #2a2a2a;
  border: 1px solid #444;
  border-radius: 12px;
  padding: 20px;
  width: 280px;
  font-size: 13px;
  color: #e9e9e9;
}

.confirm-dialog p {
  margin: 0 0 16px;
  line-height: 1.5;
}

.confirm-dialog small {
  color: #9ca3af;
  font-size: 14px;
}

.confirm-ft {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.confirm-ft button {
  height: 32px;
  padding: 0 14px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  background: #3a3a3a;
  color: #e9e9e9;
}

.confirm-ft button.danger {
  background: #773131;
  color: #fff;
}

.confirm-ft button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
